import { useAuth0 } from '@auth0/auth0-react';
import { OpenFeature, ProviderEvents } from '@openfeature/react-sdk';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';

import { CoreApiWebProvider } from './coreApiWebProvider';

import type { ReactNode } from 'react';

export function FeatureFlagsSetup({ children }: { children: ReactNode }) {
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;

    // getToken MUST return a fresh token per call (handles ~1h Auth0 expiry).
    const provider = new CoreApiWebProvider(() => getAccessTokenSilently());

    const errorHandler = (eventDetails?: { message?: string }) => {
      const msg = eventDetails?.message
        ? `Feature flag provider error: ${eventDetails.message}`
        : 'Feature flag provider error';
      Sentry.captureMessage(msg, { level: 'warning' });
      console.warn(msg);
    };

    OpenFeature.addHandler(ProviderEvents.Error, errorHandler);

    (async () => {
      // Set context first, then register + await READY.
      await OpenFeature.setContext({ targetingKey: user?.sub ?? 'anonymous' });
      if (cancelled) return;
      await OpenFeature.setProviderAndWait(provider);
    })().catch((err) => {
      // Fail open to hook defaults; never block the app on flag setup.
      console.error('Feature flag provider setup failed', err);
    });

    return () => {
      cancelled = true;
      OpenFeature.removeHandler(ProviderEvents.Error, errorHandler);
      // clearProviders() runs the provider's onClose() AND resets the default provider to NOOP.
      void OpenFeature.clearProviders();
    };
  }, [isAuthenticated, getAccessTokenSilently, user?.sub]);

  return <>{children}</>;
}
